import errorHandler from 'common/errorHandler';
import { call, put, takeEvery, takeLatest } from 'redux-saga/effects';
import { Endpoints } from 'services/api';
import * as actions from './actions';

function* createHubspotConnection(api, connection) {
    let result = yield call([api, api.post], `${Endpoints.systemConnections}/api/v1/hubspot`, connection);
    return result.data;
}

function* startHubspotOAuth(api, connectionId, returnUrl) {
    let result = yield call([api, api.post], `${Endpoints.systemConnections}/api/v1/hubspot/item/${connectionId}/oauth/start`, { returnUrl });
    return result.data;
}

function* getHubspotConnection(api, connectionId) {
    try {
        let result = yield call([api, api.get], `${Endpoints.systemConnections}/api/v1/hubspot/item/${connectionId}`);
        return result.data;
    }
    catch (error) {
        if (error.isAxiosError) {
            if (error.response && error.response.status === 404) {
                return null;
            }
        }
        throw error;
    }
}

function* updateHubspotConnection(api, connectionId, updatedConnection) {
    let response = yield call([api, api.put], `${Endpoints.systemConnections}/api/v1/hubspot/item/${connectionId}`, updatedConnection);
    return response.data;
}

function* deleteHubspotConnection(api, connectionId) {
    yield call([api, api.delete], `${Endpoints.systemConnections}/api/v1/hubspot/item/${connectionId}`);
}

function* getHubspotObjects(api, connectionId) {
    let result = yield call([api, api.get], `${Endpoints.systemConnections}/api/v1/hubspot/item/${connectionId}/objects`);
    return result.data;
}

function* getHubspotObjectDescribe(api, connectionId, objectName) {
    let result = yield call([api, api.get], `${Endpoints.systemConnections}/api/v1/hubspot/item/${connectionId}/objects/${objectName}/describe`);
    return result.data.data;
}

function* getHubspotObjectPreview(api, connectionId, objectName, fields, limit) {
    let result = yield call([api, api.post], `${Endpoints.systemConnections}/api/v1/hubspot/item/${connectionId}/preview`, { objectName, fields, limit });
    return result.data.data;
}

export function* createHubspotConnectionRequest(api, { connection }) {
    try {
        let createdConnection = yield call(createHubspotConnection, api, connection);
        yield put(actions.createHubspotConnectionSuccess(createdConnection));
    } catch (error) {
        let errorObject = errorHandler(error, "Create", "hubspot connection");
        yield put(actions.createHubspotConnectionFailure(errorObject));
    }
}

export function* startHubspotOAuthRequest(api, { connectionId, returnUrl }) {
    try {
        let result = yield call(startHubspotOAuth, api, connectionId, returnUrl);
        yield put(actions.startHubspotOAuthSuccess(result));
    } catch (error) {
        let errorObject = errorHandler(error, "Start", "hubspot oauth");
        yield put(actions.startHubspotOAuthFailure(errorObject));
    }
}

export function* getHubspotConnectionRequest(api, { connectionId }) {
    try {
        const connection = yield call(getHubspotConnection, api, connectionId);
        yield put(actions.getHubspotConnectionSuccess(connection));
    } catch (error) {
        let errorObject = errorHandler(error, "Get", "hubspot connection");
        yield put(actions.getHubspotConnectionFailure(errorObject));
    }
}

export function* updateHubspotConnectionRequest(api, { connectionId, updatedConnection }) {
    try {
        const connection = yield call(updateHubspotConnection, api, connectionId, updatedConnection);
        yield put(actions.updateHubspotConnectionSuccess(connection));
    } catch (error) {
        let errorObject = errorHandler(error, "Update", "hubspot connection");
        yield put(actions.updateHubspotConnectionFailure(errorObject));
    }
}

export function* deleteHubspotConnectionRequest(api, { connectionId }) {
    try {
        yield call(deleteHubspotConnection, api, connectionId);
        yield put(actions.deleteHubspotConnectionSuccess(connectionId));
    } catch (error) {
        let errorObject = errorHandler(error, "Delete", "hubspot connection");
        yield put(actions.deleteHubspotConnectionFailure(errorObject));
    }
}

export function* getHubspotObjectsRequest(api, { connectionId }) {
    try {
        const objects = yield call(getHubspotObjects, api, connectionId);
        yield put(actions.getHubspotObjectsSuccess(objects));
    } catch (error) {
        let errorObject = errorHandler(error, "Get", "hubspot objects");
        yield put(actions.getHubspotObjectsFailure(errorObject));
    }
}

export function* getHubspotObjectDescribeRequest(api, { connectionId, objectName }) {
    try {
        const describe = yield call(getHubspotObjectDescribe, api, connectionId, objectName);
        yield put(actions.getHubspotObjectDescribeSuccess(describe));
    } catch (error) {
        let errorObject = errorHandler(error, "Get", "hubspot object describe");
        yield put(actions.getHubspotObjectDescribeFailure(errorObject));
    }
}

export function* getHubspotObjectPreviewRequest(api, { connectionId, objectName, fields, limit }) {
    try {
        const preview = yield call(getHubspotObjectPreview, api, connectionId, objectName, fields, limit);
        yield put(actions.getHubspotObjectPreviewSuccess(preview));
    } catch (error) {
        let errorObject = errorHandler(error, "Get", "hubspot object preview");
        yield put(actions.getHubspotObjectPreviewFailure(errorObject));
    }
}

export function* watchCreateHubspotConnectionRequest(api, { params }) {
    yield call(createHubspotConnectionRequest, api, params);
}

export function* watchStartHubspotOAuthRequest(api, { params }) {
    yield call(startHubspotOAuthRequest, api, params);
}

export function* watchGetHubspotConnectionRequest(api, { params }) {
    yield call(getHubspotConnectionRequest, api, params);
}

export function* watchUpdateHubspotConnectionRequest(api, { params }) {
    yield call(updateHubspotConnectionRequest, api, params);
}

export function* watchDeleteHubspotConnectionRequest(api, { params }) {
    yield call(deleteHubspotConnectionRequest, api, params);
}

export function* watchGetHubspotObjectsRequest(api, { params }) {
    yield call(getHubspotObjectsRequest, api, params);
}

export function* watchGetHubspotObjectDescribeRequest(api, { params }) {
    yield call(getHubspotObjectDescribeRequest, api, params);
}

export function* watchGetHubspotObjectPreviewRequest(api, { params }) {
    yield call(getHubspotObjectPreviewRequest, api, params);
}

export default function* ({ api }) {
    yield takeEvery(actions.CREATE_HUBSPOT_CONNECTION_REQUEST, watchCreateHubspotConnectionRequest, api);
    yield takeEvery(actions.START_HUBSPOT_OAUTH_REQUEST, watchStartHubspotOAuthRequest, api);
    yield takeLatest(actions.GET_HUBSPOT_CONNECTION_REQUEST, watchGetHubspotConnectionRequest, api);
    yield takeLatest(actions.UPDATE_HUBSPOT_CONNECTION_REQUEST, watchUpdateHubspotConnectionRequest, api);
    yield takeLatest(actions.DELETE_HUBSPOT_CONNECTION_REQUEST, watchDeleteHubspotConnectionRequest, api);
    yield takeLatest(actions.GET_HUBSPOT_OBJECTS_REQUEST, watchGetHubspotObjectsRequest, api);
    yield takeLatest(actions.GET_HUBSPOT_OBJECT_DESCRIBE_REQUEST, watchGetHubspotObjectDescribeRequest, api);
    yield takeLatest(actions.GET_HUBSPOT_OBJECT_PREVIEW_REQUEST, watchGetHubspotObjectPreviewRequest, api);
}
